<template>

<div class="stats">

	<!-- personal summary tiles: registered players only (guests have no
	     cross-day account stats) -->
	<div v-if="signedIn && stats" class="stats-grid">
		<div class="stats-tile">
			<div class="stats-tile-value">{{ stats.played != null ? stats.played : 0 }}</div>
			<div class="stats-tile-label">Played</div>
		</div>
		<div class="stats-tile">
			<div class="stats-tile-value">{{ winPct }}</div>
			<div class="stats-tile-label">Win %</div>
		</div>
		<div class="stats-tile">
			<div class="stats-tile-value">{{ stats.streak != null ? stats.streak : 0 }}</div>
			<div class="stats-tile-label">Streak</div>
		</div>
		<div class="stats-tile">
			<div class="stats-tile-value">{{ stats.streak_max != null ? stats.streak_max : 0 }}</div>
			<div class="stats-tile-label">Max</div>
		</div>
	</div>

	<!-- tiles-to-solve distribution: the GLOBAL community result (every player,
	     guest or registered), a column per tile (1..16) + a loss column. The
	     player's own bucket is highlighted. Counts show on hover. -->
	<div class="stats-dist">

		<div class="stats-dist-head">Tiles to solve<span v-if="plays" class="stats-dist-sub"> · {{ plays }} player{{ plays === 1 ? '' : 's' }}</span></div>

		<div class="stats-dist-cols">
			<div
				v-for="row in distribution"
				:key="row.key"
				class="stats-dist-col"
				:class="{ 'is-self': row.self, 'is-loss': row.loss }"
				:title="(row.loss ? 'Failed' : (row.label + ' tile' + (row.label === '1' ? '' : 's'))) + ': ' + row.count"
			>
				<span class="stats-dist-bar-wrap">
					<span class="stats-dist-bar" :style="{ height: barHeight(row.count) }"></span>
				</span>
				<span class="stats-dist-tick">{{ row.label }}</span>
			</div>
		</div>

	</div>

	<!-- guest: nudge to sign in for personal streak tracking -->
	<div v-if="!signedIn" class="stats-nudge">
		<span class="stats-nudge-text">Sign in to track your streak and win rate across devices.</span>
		<button type="button" class="stats-nudge-btn" v-on:click="signin">Sign in</button>
	</div>

</div>

</template>

<script>

/*
	The end-screen stats panel. Two independent data sources:

	1. The "Tiles to solve" distribution is GLOBAL — the community result across
	   every player (guest or registered) for this puzzle date, from the public
	   api.request('distribution', { date }) endpoint. Shown to everyone. The
	   player's own bucket is tinted with --correct.

	2. The summary tiles (played, win %, streak, max) are PERSONAL and only
	   fetched/shown for a signed-in player (api.request('stats', {})). Guests get
	   a "sign in to track your streak" nudge instead.

	distribution objects are keyed by tile-count string ('1'..'16') plus 'loss'.
*/

import api from '../../service/api'
import bus from '../../service/bus'

var MAX_GUESSES = 16

export default {

	props: {
		// this run's guess count, so the matching distribution row can highlight.
		// null/0 for a loss or when not derivable.
		selfGuesses: {
			default: null
		},
		// whether this run was a solve (only solves land in a numbered bucket)
		selfSolved: {
			type: Boolean,
			default: false
		}
	},

	data: function() {

		return {
			// personal card (signed-in only)
			stats: null,
			// global community aggregate: { distribution, plays, solved }
			dist: null
		}

	},

	computed: {

		signedIn: function() {

			return this.$store.getters['account/signedIn']

		},

		// the puzzle date this end screen belongs to, for the distribution fetch
		puzzleDate: function() {

			var today = this.$store.getters['game/today']

			return (today && today.date) || ''

		},

		// how many players are in the global distribution (for the subheading)
		plays: function() {

			return (this.dist && this.dist.plays) || 0

		},

		winPct: function() {

			if (!this.stats) return 0

			// prefer the server's win_pct; else derive from played/wins
			if (this.stats.win_pct != null) return Math.round(this.stats.win_pct)

			var played = this.stats.played || 0

			if (!played) return 0

			return Math.round(((this.stats.wins || 0) / played) * 100)

		},

		/*
			Normalise the GLOBAL distribution object into an ordered row list:
			tile counts 1..16, then a 'loss' row. The own-run bucket (if a solve)
			is flagged for highlight.
		*/
		distribution: function() {

			var dist = (this.dist && this.dist.distribution) || {}
			var rows = []

			var ownKey = (this.selfSolved && this.selfGuesses) ? String(this.selfGuesses) : null

			// always show all 16 tile buckets (one bar per tile, 1..16)
			for (var g = 1; g <= MAX_GUESSES; g++) {

				var key = String(g)

				rows.push({
					key: key,
					label: key,
					count: dist[key] || 0,
					loss: false,
					self: key === ownKey
				})

			}

			// always show a loss row so the distribution reads honestly
			rows.push({
				key: 'loss',
				label: 'X',
				count: dist.loss || 0,
				loss: true,
				self: !this.selfSolved && this.selfGuesses != null
			})

			return rows

		},

		maxCount: function() {

			var max = 0

			this.distribution.forEach(function(row) {

				if (row.count > max) max = row.count

			})

			return max

		}

	},

	created: function() {

		// the global community distribution is fetched for everyone
		this.fetchDistribution()

		// personal summary tiles are signed-in only
		if (this.signedIn) this.fetchStats()

	},

	methods: {

		// the GLOBAL community distribution for this puzzle date (public endpoint)
		fetchDistribution: function() {

			var self = this

			api.request('distribution', { date: this.puzzleDate }).then(function(json) {

				if (json && !json.error) {

					self.dist = {
						distribution: json.distribution || {},
						plays: json.plays || 0,
						solved: json.solved || 0
					}

				} else {

					self.dist = { distribution: {}, plays: 0, solved: 0 }

				}

			})

		},

		// the signed-in player's personal card (played / win % / streak / max)
		fetchStats: function() {

			var self = this

			api.request('stats', {}).then(function(json) {

				if (json && !json.error) self.stats = json

			})

		},

		// bar height proportional to the busiest bucket (min 6% so any non-zero
		// count is visible); 0 for an empty bucket
		barHeight: function(count) {

			if (!this.maxCount || !count) return '0%'

			var pct = (count / this.maxCount) * 100

			return Math.max(6, pct) + '%'

		},

		// open the one sign-in dialog (App.vue hosts it; the Topbar opens it the
		// same way) so a guest can start tracking stats without leaving the reveal
		signin: function() {

			bus.$emit('signin')

		}

	}

}

</script>

<style scoped>

.stats {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

/* guest nudge: a quiet row under the chart, not a blocking prompt */
.stats-nudge {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 14px;
	border: 1px solid var(--hairline);
	border-radius: var(--radius);
	background: #fbf8f1;
}

.stats-nudge-text {
	flex: 1;
	font-family: var(--font-body);
	font-size: 12.5px;
	line-height: 1.4;
	color: var(--muted-2);
}

.stats-nudge-btn {
	flex: none;
	height: 36px;
	padding: 0 16px;
	border: none;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.stats-nudge-btn:hover {
	background: var(--accent);
}

.stats-loading {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted);
	text-align: center;
	padding: 8px 0;
}

/* summary tiles */
.stats-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
}

.stats-tile {
	text-align: center;
	padding: 12px 4px;
	border: 1px solid var(--hairline);
	border-radius: var(--radius);
}

.stats-tile-value {
	font-family: var(--font-mono);
	font-size: 20px;
	color: var(--ink);
}

.stats-tile-label {
	font-family: var(--font-mono);
	font-size: 8.5px;
	letter-spacing: 0.08em;
	color: var(--muted);
	text-transform: uppercase;
	margin-top: 3px;
}

/* distribution */
.stats-dist-head {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.14em;
	color: var(--muted);
	text-transform: uppercase;
	margin-bottom: 12px;
}

.stats-dist-sub {
	color: var(--muted-2);
}

/* a column per tile (1..16) + a loss column; counts on hover */
.stats-dist-cols {
	display: flex;
	align-items: flex-end;
	gap: 3px;
	height: 96px;
}

.stats-dist-col {
	flex: 1;
	min-width: 0;
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: default;
}

/* a small gap setting the loss column apart */
.stats-dist-col.is-loss {
	margin-left: 6px;
}

.stats-dist-bar-wrap {
	flex: 1;
	width: 100%;
	display: flex;
	align-items: flex-end;
	justify-content: center;
}

.stats-dist-bar {
	width: 74%;
	background: var(--accent);
	border-radius: 2px 2px 0 0;
	transition: height 320ms ease;
}

.stats-dist-tick {
	margin-top: 4px;
	font-family: var(--font-mono);
	font-size: 8px;
	color: var(--muted);
}

.stats-dist-col.is-self .stats-dist-bar {
	background: var(--correct);
}

.stats-dist-col.is-self .stats-dist-tick {
	color: var(--correct);
}

.stats-dist-col.is-loss .stats-dist-bar {
	background: var(--wrong);
}

.stats-dist-col.is-loss .stats-dist-tick {
	color: var(--wrong);
}

@media (prefers-reduced-motion: reduce) {

	.stats-dist-bar {
		transition: none;
	}

}

</style>
